// src/utils/seoConfig.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SeoConfig component for managing all SEO-related tags
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.canonicalUrl - Canonical URL for the page
 * @param {Object} props.openGraph - Open Graph metadata
 * @param {Object} props.twitter - Twitter card metadata
 * @param {Object} props.jsonLd - JSON-LD structured data
 */
export const SeoConfig = ({
  title,
  description,
  canonicalUrl,
  image,
  openGraph,
  twitter,
  jsonLd,
  noindex = false
}) => {
  // Construct full image URL if provided
  const imageUrl = image && !image.startsWith('http') 
    ? `${window.location.origin}${image}` 
    : image;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots directives */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={openGraph?.type || 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={openGraph?.title || title} />
      <meta property="og:description" content={openGraph?.description || description} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {openGraph?.locale && <meta property="og:locale" content={openGraph.locale} />}
      {openGraph?.site_name && <meta property="og:site_name" content={openGraph.site_name} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitter?.cardType || "summary_large_image"} />
      {twitter?.handle && <meta name="twitter:creator" content={twitter.handle} />}
      {twitter?.site && <meta name="twitter:site" content={twitter.site} />}
      <meta name="twitter:title" content={twitter?.title || title} />
      <meta name="twitter:description" content={twitter?.description || description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      
      {/* Structured Data / JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      
      {/* Additional meta tags for better SEO */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <html lang="en" />
    </Helmet>
  );
};

/**
 * Generate BlogPosting JSON-LD structured data
 */
export const generateBlogJsonLd = (post, siteUrl) => {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image.startsWith('http') ? post.image : `${siteUrl}${post.image}`,
    "datePublished": new Date(post.date).toISOString(),
    "dateModified": post.lastUpdated ? new Date(post.lastUpdated).toISOString() : new Date(post.date).toISOString(),
    "author": {
      "@type": "Person",
      "name": post.author,
      "jobTitle": post.authorRole
    },
    "publisher": {
      "@type": "Organization",
      "name": "Galo Industries",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/img/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/${post.slug}`
    },
    "keywords": post.tags ? post.tags.join(", ") : ""
  };
};

/**
 * Generate Organization JSON-LD structured data
 */
export const generateOrganizationJsonLd = (siteUrl) => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Galo Industries",
    "url": siteUrl,
    "logo": `${siteUrl}/img/logo.png`,
    "sameAs": [
      "https://www.facebook.com/galoindustries",
      "https://www.linkedin.com/company/galo-industries",
      "https://twitter.com/galoindustries"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-123-456-7890",
      "contactType": "customer service",
      "availableLanguage": ["English"]
    }
  };
};

/**
 * Generate BreadcrumbList JSON-LD structured data
 */
export const generateBreadcrumbsJsonLd = (items, siteUrl) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteUrl}${item.path}`
    }))
  };
};